import { buildChatResumeOptions } from "../../../utils/chatResumeOptions";
import { useChatStore } from "@metalmind-ab/mmagent-chat-ui";
import type { StreamTroubleKind } from "../../../services/frontendErrorReporting";

let conversationSseLoggerFetch: typeof window.fetch | null = null;

type StreamTroubleReporter = (
  eventKind: StreamTroubleKind,
  details: {
    attempt: number;
    elapsedMs: number;
    lastEventType: string;
    workspaceId?: string;
  },
) => void;

interface ConversationStreamDiagnosticState {
  turnStartedAt: number;
  lastEventType: string;
  assistantTurnId?: string;
  submissionId?: string;
}

interface ActivePostStreamDiagnostic {
  streamId: string;
  state: ConversationStreamDiagnosticState;
}

const MAX_STREAM_DIAGNOSTICS = 50;
const streamDiagnostics = new Map<string, ConversationStreamDiagnosticState>();
const activePostStreamByConversation = new Map<
  string,
  ActivePostStreamDiagnostic
>();
let nextObservedStreamId = 0;
let pageIsClosing = false;
let pageCloseListenerInstalled = false;

function markPageClosing(): void {
  pageIsClosing = true;
}

function resetPageClosing(): void {
  pageIsClosing = false;
}

function installPageCloseListener(): void {
  if (typeof window === "undefined" || pageCloseListenerInstalled) {
    return;
  }
  window.addEventListener("pagehide", markPageClosing);
  window.addEventListener("pageshow", resetPageClosing);
  pageCloseListenerInstalled = true;
}

function ensureConversationStreamDiagnostic(
  streamId: string,
  turnStartedAt = Date.now(),
): ConversationStreamDiagnosticState {
  const state = streamDiagnostics.get(streamId);
  if (state) return state;
  const created = { turnStartedAt, lastEventType: "none" };
  streamDiagnostics.set(streamId, created);
  enforceStreamDiagnosticLimit();
  return created;
}

function enforceStreamDiagnosticLimit(): void {
  while (streamDiagnostics.size > MAX_STREAM_DIAGNOSTICS) {
    const oldestStreamId = streamDiagnostics.keys().next().value;
    if (oldestStreamId === undefined) return;
    streamDiagnostics.delete(oldestStreamId);
  }
  while (activePostStreamByConversation.size > MAX_STREAM_DIAGNOSTICS) {
    const oldestConversationId = activePostStreamByConversation
      .keys()
      .next().value;
    if (oldestConversationId === undefined) return;
    activePostStreamByConversation.delete(oldestConversationId);
  }
}

function clearActivePostStream(
  conversationId: string,
  expectedStreamId?: string,
): void {
  const active = activePostStreamByConversation.get(conversationId);
  if (!active || (expectedStreamId && active.streamId !== expectedStreamId)) {
    return;
  }
  activePostStreamByConversation.delete(conversationId);
}

export function recordConversationStreamTurnStart(
  conversationId: string,
  startedAt = Date.now(),
  streamId = conversationId,
): void {
  const previous = activePostStreamByConversation.get(conversationId);
  if (previous && previous.streamId !== streamId) {
    streamDiagnostics.delete(previous.streamId);
  }
  const state = {
    turnStartedAt: startedAt,
    lastEventType: "none",
  };
  streamDiagnostics.set(streamId, state);
  activePostStreamByConversation.delete(conversationId);
  activePostStreamByConversation.set(conversationId, { streamId, state });
  enforceStreamDiagnosticLimit();
}

export function recordConversationStreamEvent(
  conversationId: string,
  eventType: string,
  streamId = activePostStreamByConversation.get(conversationId)?.streamId ??
    conversationId,
  identifiers: { assistantTurnId?: string; submissionId?: string } = {},
): void {
  const state = ensureConversationStreamDiagnostic(streamId);
  const lastEventType = eventType.slice(0, 80) || "unknown";
  state.lastEventType = lastEventType;
  const activePost = activePostStreamByConversation.get(conversationId);
  if (!activePost) return;

  const matchesActiveTurn =
    activePost.streamId === streamId ||
    (identifiers.assistantTurnId !== undefined &&
      identifiers.assistantTurnId === activePost.state.assistantTurnId) ||
    (identifiers.submissionId !== undefined &&
      identifiers.submissionId === activePost.state.submissionId);
  if (!matchesActiveTurn) return;

  if (identifiers.assistantTurnId) {
    activePost.state.assistantTurnId = identifiers.assistantTurnId;
  }
  if (identifiers.submissionId) {
    activePost.state.submissionId = identifiers.submissionId;
  }
  activePost.state.lastEventType = lastEventType;
  if (
    eventType === "done" ||
    eventType === "cancelled" ||
    eventType === "queue_item_cancelled" ||
    eventType === "queue_item_failed"
  ) {
    clearActivePostStream(conversationId, activePost.streamId);
  }
}

export function reportIdaStreamTrouble(
  eventKind: StreamTroubleKind,
  attempt: number,
  workspaceId: string | undefined,
  reporter?: StreamTroubleReporter,
  now = Date.now(),
  conversationIdOverride?: string | number | null,
): void {
  const conversationId =
    conversationIdOverride ?? useChatStore.getState().conversationId;
  if (conversationId === null || conversationId === undefined) return;
  const conversationKey = String(conversationId);
  const activePost = activePostStreamByConversation.get(conversationKey);
  if (eventKind === "stream_dropped" && !activePost) return;
  const state =
    activePost?.state ?? ensureConversationStreamDiagnostic(conversationKey);
  if (
    eventKind === "stream_dropped" &&
    (state.lastEventType === "done" || state.lastEventType === "error")
  ) {
    clearActivePostStream(conversationKey, activePost?.streamId);
    return;
  }
  if (eventKind === "stream_dropped" && pageIsClosing) {
    return;
  }
  reporter?.(eventKind, {
    attempt,
    elapsedMs: Math.max(0, now - state.turnStartedAt),
    lastEventType: state.lastEventType,
    workspaceId,
  });
  if (eventKind === "stream_retries_exhausted") {
    clearActivePostStream(conversationKey);
  }
}

export function clearConversationStreamTurn(
  conversationId: string | number | null | undefined,
): void {
  if (conversationId === null || conversationId === undefined) return;
  clearActivePostStream(String(conversationId));
}

export function isIdaStreamRetryExhaustion(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    message ===
      "Connection lost during streaming. Refresh to see the latest response." ||
    message === "The conversation stream disconnected. Use Retry to reconnect."
  );
}

export function resetConversationStreamDiagnosticsForTests(): void {
  streamDiagnostics.clear();
  activePostStreamByConversation.clear();
  pageIsClosing = false;
  nextObservedStreamId = 0;
  if (pageCloseListenerInstalled && typeof window !== "undefined") {
    window.removeEventListener("pagehide", markPageClosing);
    window.removeEventListener("pageshow", resetPageClosing);
  }
  pageCloseListenerInstalled = false;
}

export function getConversationStreamDiagnosticCountsForTests(): {
  streams: number;
  activePosts: number;
} {
  return {
    streams: streamDiagnostics.size,
    activePosts: activePostStreamByConversation.size,
  };
}

const conversationSseActivityListeners = new Set<
  (conversationId: string) => void
>();

export function subscribeToConversationSseActivity(
  listener: (conversationId: string) => void,
): () => void {
  conversationSseActivityListeners.add(listener);
  return () => conversationSseActivityListeners.delete(listener);
}

export function notifyConversationStreamRecovery(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("ida-conversation-stream-recovery"));
  }
}

export interface ObservedConversationSseFrame {
  type: string;
  source?: "post" | "subscription";
  conversation_id?: string | number;
  assistant_turn_id?: string;
  submission_id?: string;
  voice_call_id?: string;
  call_id?: string;
  tool_name?: string;
  label?: string;
  position?: number;
  status?: string;
  reason?: string;
  job_id?: string;
  skill_name?: string;
  progress?: number;
  session_id?: string;
  voice_session_id?: string;
  notice_persisted?: boolean;
}

const conversationSseFrameListeners = new Set<
  (frame: ObservedConversationSseFrame) => void
>();

export function subscribeToConversationSseFrames(
  listener: (frame: ObservedConversationSseFrame) => void,
): () => void {
  conversationSseFrameListeners.add(listener);
  return () => conversationSseFrameListeners.delete(listener);
}

/** Stable, content-free identifier for development diagnostics. */
export function hashDiagnosticId(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

function isChatMessageStreamRequest(input: RequestInfo | URL): boolean {
  const url =
    typeof input === "string" || input instanceof URL
      ? String(input)
      : input.url;
  return (
    /\/chat\/conversations\/[^/]+\/messages\/stream(?:[?#]|$)/.test(url) ||
    /\/chat\/messages\/stream(?:[?#]|$)/.test(url)
  );
}

function getConversationIdFromStreamRequest(
  input: RequestInfo | URL,
): string | null {
  const url =
    typeof input === "string" || input instanceof URL
      ? String(input)
      : input.url;
  return (
    url.match(
      /\/chat\/conversations\/([^/]+)\/messages\/stream(?:[?#]|$)/,
    )?.[1] ?? null
  );
}

function reportConversationSseActivity(conversationId: string): void {
  conversationSseActivityListeners.forEach((listener) => {
    try {
      listener(conversationId);
    } catch {
      // Activity observation must never affect the application's stream.
    }
  });
}

export async function logConversationSseFrames(
  response: Response,
  conversationId?: string | null,
  source: "post" | "subscription" = "subscription",
  streamId?: string,
): Promise<void> {
  if (!response.body) {
    if (streamId) streamDiagnostics.delete(streamId);
    return;
  }
  const diagnosticStreamId =
    streamId ??
    (conversationId
      ? `${conversationId}:observed:${++nextObservedStreamId}`
      : undefined);
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let lastEventType = "none";
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value?.byteLength && conversationId) {
        reportConversationSseActivity(conversationId);
      }
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop() ?? "";
      for (const line of lines) {
        if (!line.startsWith("data:")) continue;
        try {
          const frame = JSON.parse(line.slice(5).trim()) as Record<
            string,
            unknown
          >;
          const type = typeof frame.type === "string" ? frame.type : "unknown";
          lastEventType = type;
          if (conversationId) {
            recordConversationStreamEvent(
              conversationId,
              type,
              diagnosticStreamId ?? conversationId,
              {
                assistantTurnId:
                  typeof frame.assistant_turn_id === "string"
                    ? frame.assistant_turn_id
                    : undefined,
                submissionId:
                  typeof frame.submission_id === "string"
                    ? frame.submission_id
                    : undefined,
              },
            );
          }
          if (
            type === "queued" ||
            type === "assistant_turn_started" ||
            type === "tool_composing" ||
            type === "tool_calling" ||
            type === "tool_start" ||
            type === "content_delta" ||
            type === "queue_item_failed" ||
            type === "job_started" ||
            type === "job_progress" ||
            type === "job_completed" ||
            type === "job_failed" ||
            type === "done" ||
            type === "cancelled" ||
            type === "queue_item_cancelled" ||
            type === "error" ||
            type === "voice_session_ended"
          ) {
            const observedFrame: ObservedConversationSseFrame = {
              type,
              ...(source === "post" ? { source } : {}),
              ...(conversationId
                ? { conversation_id: conversationId }
                : typeof frame.conversation_id === "string" ||
                    typeof frame.conversation_id === "number"
                  ? { conversation_id: frame.conversation_id }
                  : {}),
              ...(typeof frame.assistant_turn_id === "string"
                ? { assistant_turn_id: frame.assistant_turn_id }
                : {}),
              ...(typeof frame.voice_call_id === "string"
                ? { voice_call_id: frame.voice_call_id }
                : {}),
              ...(typeof frame.submission_id === "string"
                ? { submission_id: frame.submission_id }
                : {}),
              ...(typeof frame.call_id === "string"
                ? { call_id: frame.call_id }
                : typeof frame.tool_call_id === "string"
                  ? { call_id: frame.tool_call_id }
                  : {}),
              ...(typeof frame.tool_name === "string"
                ? { tool_name: frame.tool_name }
                : {}),
              ...(typeof frame.label === "string"
                ? { label: frame.label }
                : {}),
              ...(typeof frame.position === "number"
                ? { position: frame.position }
                : {}),
              ...(typeof frame.status === "string"
                ? { status: frame.status }
                : {}),
              ...(typeof frame.reason === "string"
                ? { reason: frame.reason }
                : {}),
              ...(typeof frame.job_id === "string"
                ? { job_id: frame.job_id }
                : {}),
              ...(typeof frame.skill_name === "string"
                ? { skill_name: frame.skill_name }
                : {}),
              ...(typeof frame.progress === "number"
                ? { progress: frame.progress }
                : {}),
              ...(typeof frame.session_id === "string"
                ? { session_id: frame.session_id }
                : {}),
              ...(typeof frame.voice_session_id === "string"
                ? { voice_session_id: frame.voice_session_id }
                : {}),
              ...(typeof frame.notice_persisted === "boolean"
                ? { notice_persisted: frame.notice_persisted }
                : {}),
            };
            conversationSseFrameListeners.forEach((listener) => {
              try {
                listener(observedFrame);
              } catch (error) {
                console.error(
                  error,
                  "Failed to handle an observed conversation SSE frame",
                );
              }
            });
          }
          const assistantTurnId =
            typeof frame.assistant_turn_id === "string"
              ? frame.assistant_turn_id
              : null;
          if (process.env.NODE_ENV === "development") {
            console.debug("[IDA Chat] conversation SSE frame", {
              type,
              assistant_turn_id_hash: assistantTurnId
                ? hashDiagnosticId(assistantTurnId)
                : null,
              submission_id_present: typeof frame.submission_id === "string",
              arrival_time: new Date().toISOString(),
            });
          }
        } catch {
          // Ignore non-JSON keepalives; never log raw frame data.
        }
      }
    }
  } catch {
    // Observation is best-effort and must not affect the application's stream.
  } finally {
    reader.releaseLock();
    if (diagnosticStreamId) streamDiagnostics.delete(diagnosticStreamId);
    if (source === "post" && conversationId && lastEventType === "done") {
      clearActivePostStream(conversationId, diagnosticStreamId);
    }
  }
}

/** Development-only, content-free telemetry for diagnosing conversation SSE ordering. */
export function installIdaConversationSseFrameLogger(): void {
  if (
    typeof window === "undefined" ||
    window.fetch === conversationSseLoggerFetch
  ) {
    return;
  }

  if (process.env.NODE_ENV === "development") {
    console.debug("[IDA Chat] conversation SSE frame logger attached");
  }
  const originalFetch = window.fetch.bind(window);
  const observedFetch: typeof window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ) => {
    const requestStartedAt = Date.now();
    const response = await originalFetch(input, init);
    const isChatStream = isChatMessageStreamRequest(input);
    const requestConversationId = isChatStream
      ? getConversationIdFromStreamRequest(input)
      : null;
    // The first message in a new chat uses /chat/messages/stream, which has no
    // conversation id in its URL. The API supplies the created id on the
    // response; only observe frames when one of these trusted sources scopes
    // the stream to a conversation.
    const conversationId =
      requestConversationId ??
      (isChatStream
        ? response.headers.get("X-Chat-Conversation-ID")?.trim() || null
        : null);
    if (conversationId) {
      const requestMethod =
        init?.method ??
        (typeof Request !== "undefined" && input instanceof Request
          ? input.method
          : "GET");
      const streamId = `${conversationId}:${requestMethod.toUpperCase()}:${++nextObservedStreamId}`;
      if (requestMethod.toUpperCase() === "POST") {
        recordConversationStreamTurnStart(
          conversationId,
          requestStartedAt,
          streamId,
        );
      }
      try {
        // Both POST turns and passive GET subscriptions deliver keepalive
        // comments through the body. Read a clone so chunk activity resets the
        // idle clock without consuming the package's response stream.
        void logConversationSseFrames(
          response.clone(),
          conversationId,
          requestMethod.toUpperCase() === "POST" ? "post" : "subscription",
          streamId,
        );
      } catch {
        // Cloning for diagnostics must never interfere with the response.
      }
    }
    return response;
  };
  conversationSseLoggerFetch = observedFetch;
  window.fetch = observedFetch;
}

/** X-Machine uses the conversation stream as the sole path for IDA turns. */
export function buildIdaChatStreamConfig(
  apiBaseUrl: string,
  options: {
    workspaceId?: string;
    reportStreamTrouble?: StreamTroubleReporter;
  } = {},
) {
  installPageCloseListener();
  installIdaConversationSseFrameLogger();
  return {
    ...buildChatResumeOptions(apiBaseUrl),
    enableConversationSubscription: true,
    enableAutoReconnect: true,
    onStreamDropped: (attempt: number) => {
      reportIdaStreamTrouble(
        "stream_dropped",
        attempt,
        options.workspaceId,
        options.reportStreamTrouble,
      );
      notifyConversationStreamRecovery();
    },
  };
}
